import type { NavigateFunction } from '@remix-run/react';
import type { TNavbarDropdownElementItem, TNavbarItemElement } from '@shared/dream-components';
import { produce } from 'immer';

import type { Subscription } from '~/interfaces/subscriptions';
import logout from '~/utils/logout';

export const updateNodeAttributes = (
  node: TNavbarItemElement | TNavbarDropdownElementItem,
  isLoggedIn: boolean,
  isPremiumUser: boolean,
  subscription: Subscription | undefined,
): TNavbarItemElement | TNavbarDropdownElementItem => {
  // Early return if node is falsy or doesn't have attrs - nothing to update
  // Note: attrs is optional in the type definition, and we've seen 11k+ Sentry errors
  // where attrs is undefined in production (legacy/malformed navbar data).
  // Without build-time type checking in Vite, we handle this defensively.
  if (!node || !node.attrs) {
    return node;
  }

  // Use produce directly - no need for deepClone since immer handles immutability
  // The previous deepClone was causing attrs to be lost (JSON.stringify drops certain properties)
  return produce(node, (draft) => {
    // TypeScript can't infer that draft.attrs exists based on the outer check
    // Add defensive check for type safety and runtime protection
    if (!draft.attrs) {
      return draft;
    }

    const showWhen = node.attrs?.showWhen;

    // set up visibility based on showWhen
    if (showWhen) {
      if (showWhen === 'all') {
        draft.attrs.visible = true;
      } else if (showWhen === 'all_logged_out') {
        draft.attrs.visible = !isLoggedIn;
      } else if (showWhen === 'all_logged_in') {
        draft.attrs.visible = isLoggedIn;
      } else if (showWhen === 'paid') {
        draft.attrs.visible = isPremiumUser && isLoggedIn;
      } else if (showWhen === 'free') {
        draft.attrs.visible = !isPremiumUser && isLoggedIn;
      }
    } else {
      // if showWhen is not set, default to based on actions
      draft.attrs.visible = true;

      if (
        draft.attrs.action === 'login' ||
        (draft.attrs.action as string) === 'sign_in' || // `sign_in` is WRONG, the corrent one is `login`, this is to support old data
        draft.attrs.action === 'sign_up'
      ) {
        draft.attrs.visible = !isLoggedIn;
      }

      if (draft.attrs.action === 'profile') {
        draft.attrs.visible = isLoggedIn;
      }

      if (draft.attrs.action === 'logout') {
        draft.attrs.visible = isLoggedIn;
      }
    }

    // Ensure the button is not open in a new tab if it has an action
    const isOpenAction =
      draft.attrs.onClick === 'action' || (draft.attrs.onClick === undefined && !!draft.attrs.action);
    if (isOpenAction && 'new_tab' in draft.attrs) {
      draft.attrs.new_tab = false;
    }

    // Set avatar and subscription details
    if (
      draft.type === 'navbar_item' &&
      draft.attrs &&
      isOpenAction &&
      isLoggedIn &&
      draft.attrs.action === 'profile' &&
      draft.attrs.type === 'user_button'
    ) {
      draft.attrs.avatar = subscription?.avatar_url;
      draft.attrs.firstName = subscription?.first_name;
      draft.attrs.lastName = subscription?.last_name;
      draft.attrs.email = subscription?.email;

      if (draft.attrs.style === 'button') {
        draft.attrs.href = undefined;
      } else {
        draft.attrs.href = `/subscribe/${subscription?.subscriber_id}/manage`;
      }
    }

    return draft;
  });
};

export const getClientSideNavigationHandler = (
  node: TNavbarItemElement | TNavbarDropdownElementItem,
  subscription: Subscription | undefined,
  navigate: NavigateFunction,
): (() => void) | undefined => {
  const onClick = node?.attrs && 'onClick' in node.attrs ? node.attrs.onClick : undefined;
  const action = node?.attrs && 'action' in node.attrs && node.attrs.action;
  const currentHref = node.attrs?.href || '';
  const isOpenAction = onClick === 'action' || (onClick === undefined && !!action);
  let url: URL | undefined;
  let handleClientSideNavigation: any;
  const navigateOptions: any = {};
  const isCustomLink = node.attrs?.isCustomLink;

  try {
    url = new URL(currentHref);
    // grab all the query params and hash of current window location
    // if it's internal linking, we want to keep app state.
    if (typeof window !== 'undefined' && !isCustomLink) {
      const currentWindow = new URL(window.location.href);
      url.search = currentWindow.search;
      url.hash = currentWindow.hash;
    }
  } catch {
    // Fail silently
  }

  const handleLogout = () => {
    logout('token');
  };

  // On actions we only want to
  if (isOpenAction) {
    if (typeof window !== 'undefined') {
      url = new URL(window.location.href);
    }
    if (action === 'login' && url) {
      url.searchParams.set('modal', 'login');
      navigateOptions.preventScrollReset = true;
    } else if (action === 'sign_up' && url) {
      url.searchParams.set('modal', 'signup');
      navigateOptions.preventScrollReset = true;
    } else if (action === 'profile' && url) {
      url.pathname = `subscribe/${subscription?.subscriber_id || ''}/manage`;
    } else if (action === 'search' && url) {
      url.searchParams.set('modal', 'search');
      navigateOptions.preventScrollReset = true;
    } else if (action === 'logout') {
      url = undefined;
      handleClientSideNavigation = handleLogout;
    }
  } else if (!url && !isCustomLink) {
    // we log error here because this is an invalid state
    //console.error('ERROR: no url for node.attrs:', node.attrs);
  }

  if (!(node.attrs && 'new_tab' in node.attrs && node.attrs?.new_tab) && url && !isCustomLink) {
    handleClientSideNavigation = () => {
      navigate(url.pathname + url.search + url.hash, navigateOptions);
    };
  }

  if (
    node.attrs &&
    'type' in node.attrs &&
    node.attrs?.type === 'user_button' &&
    node.attrs?.action === 'profile' &&
    node.attrs?.style === 'dropdown'
  ) {
    handleClientSideNavigation = undefined;
  }

  return handleClientSideNavigation;
};

export const getNodeActionAndVisibility = (
  node: TNavbarItemElement | TNavbarDropdownElementItem,
  subscription: Subscription | undefined,
  navigate: NavigateFunction,
) => {
  return {
    handleClientSideNavigation: getClientSideNavigationHandler(node, subscription, navigate),
  };
};
